import { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  IncomingStatisticsRequest,
  getInboundTaskTypes,
  getIncomingStatistics,
  selectInboundTaskTypeOptions,
} from '../../../store/reducers/IncomingStatisticsSlice';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { selectClinics, getClinics } from '../../../store/reducers/ClinicSlice';
import { formatDate } from '../../../utils';
import { useFilterOption } from '../../shared/useFilterOption';
import { RangeValue } from './types';
import {
  buildIncomingStatisticRows,
  InboundStatisticRow,
} from '../../../components/IncomingStatisticsV2/IncomingStatistics/buildIncomingStatisticRows';
import { shouldShowQuestionnaireTemplate } from '../../../models/Task/scenarioMapping';
import { retainTaskCampaignFilters } from '../../../models/Task/campaignFilters';
import type { TaskCampaignFilters } from '../../../models/Task/campaignFilters';

export const useIncomingStatistics = () => {
  const incomingStatistics = useAppSelector(
    (state) => state.incomingStatistics,
  );
  const clinics = useAppSelector(selectClinics);
  const apiScenarioOptions = useAppSelector(selectInboundTaskTypeOptions);

  const [statisticsRequest, setStatisticsRequest] =
    useState<IncomingStatisticsRequest>({
      From: dayjs().format('YYYY-MM-DD'),
      To: dayjs().format('YYYY-MM-DD'),
      ClinicIds: [],
    });

  const dispatch = useAppDispatch();
  const [selectedClinics, setSelectedClinics] = useState<string[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);

  useEffect(() => {
    dispatch(getClinics());
    dispatch(getInboundTaskTypes());
  }, [dispatch]);

  useEffect(() => {
    const request = dispatch(getIncomingStatistics(statisticsRequest));

    return () => {
      request?.abort();
    };
  }, [dispatch, statisticsRequest]);

  const scenarioOptions = apiScenarioOptions;

  const dataSource: InboundStatisticRow[] = useMemo(
    () => buildIncomingStatisticRows(incomingStatistics, selectedScenario),
    [incomingStatistics, selectedScenario],
  );

  const dateRange = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(statisticsRequest.From), dayjs(statisticsRequest.To)],
    [statisticsRequest.From, statisticsRequest.To],
  );

  const onClinicsChange = (value: string[]) => {
    setSelectedClinics(value);
    if (Array.isArray(value) && value.length === 0) {
      handleClinics(value);
    }
  };

  const handleClinics = useCallback(
    (e: string[]) => {
      setStatisticsRequest((prev) => ({
        ...prev,
        ClinicIds: e,
      }));
    },
    [setStatisticsRequest],
  );

  const handleRangePicker = useCallback(
    (values: RangeValue<Dayjs>, formatString: string[]) => {
      if (!values || !formatString[0] || !formatString[1]) {
        return;
      }

      setStatisticsRequest((prev) => ({
        ...prev,
        From: formatDate(formatString[0]),
        To: formatDate(formatString[1]),
      }));
    },
    [setStatisticsRequest],
  );

  const filterOption = useFilterOption();

  const handleScenarioChange = useCallback(
    (value: string | undefined) => {
      const nextScenario = value ?? null;

      setSelectedScenario(nextScenario);
      const nextTypes = nextScenario ? [nextScenario] : [];
      setStatisticsRequest((prev) => {
        const {
          Types: _types,
          FormBatchIds: _formBatchIds,
          DispensarizationBatchIds: _dispensarizationBatchIds,
          FormTemplateIds: _formTemplateIds,
          ...rest
        } = prev;
        return {
          ...rest,
          ...(nextTypes.length ? { Types: nextTypes } : {}),
          ...retainTaskCampaignFilters(nextTypes, prev),
        };
      });
    },
    [setStatisticsRequest],
  );

  const onCampaignFiltersChange = useCallback((value: TaskCampaignFilters) => {
    setStatisticsRequest((prev) => {
      const {
        FormBatchIds: _formBatchIds,
        DispensarizationBatchIds: _dispensarizationBatchIds,
        ...rest
      } = prev;
      return {
        ...rest,
        ...retainTaskCampaignFilters(prev.Types ?? [], {
          ...value,
          FormTemplateIds: prev.FormTemplateIds,
        }),
      };
    });
  }, []);

  const campaignFilters = useMemo(
    () => ({
      FormBatchIds: statisticsRequest.FormBatchIds,
      DispensarizationBatchIds: statisticsRequest.DispensarizationBatchIds,
    }),
    [
      statisticsRequest.FormBatchIds,
      statisticsRequest.DispensarizationBatchIds,
    ],
  );

  const handleOpenClinics = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClinics(selectedClinics);
      }
    },
    [selectedClinics, handleClinics],
  );

  const showTemplateFilter = shouldShowQuestionnaireTemplate(selectedScenario);

  const canExportStatistics =
    !incomingStatistics?.status?.responseLoading &&
    !incomingStatistics?.status?.responseError &&
    incomingStatistics?.lastSuccessfulRequest !== undefined &&
    JSON.stringify(incomingStatistics.lastSuccessfulRequest) ===
      JSON.stringify(statisticsRequest);

  return {
    dataSource,
    clinics,
    scenarioOptions,
    handleClinics,
    handleRangePicker,
    dateRange,
    selectedClinics,
    selectedScenario,
    campaignFilters,
    onClinicsChange,
    handleScenarioChange,
    onCampaignFiltersChange,
    filterOption,
    handleOpenClinics,
    showTemplateFilter,
    statisticsRequest,
    isLoading: incomingStatistics?.status?.responseLoading ?? false,
    canExportStatistics,
  };
};
